import { Component, h, Prop, Host, Element, State } from '@stencil/core';
import classNames from 'classnames';

export interface AhaComment extends HTMLAhaCommentElement {}

@Component({
  tag: 'aha-comment',
  styleUrl: 'aha-comment.css',
  shadow: true,
})
export class AhaComment {
  @Element() host!: HTMLAhaCommentElement;

  /** The date of the comment. */
  @Prop() commentDate?: string;

  /** Whether the comment has replies or not. */
  @Prop() hasReplies?: boolean;

  /** Whether to show the leading and trailing lines. */
  @Prop() isChild: boolean = false;

  /** Whether replies are enabled for this comment. */
  @Prop() repliesEnabled: boolean = false;

  /** Whether the reply editor is open */
  @State() isReplying: boolean = false;

  componentDidLoad() {
    // Handle hover state for share controls
    // get shadow root elements
    const shadowRoot = this.host.shadowRoot;
    if (shadowRoot) {
      const repliesElement = shadowRoot.querySelector('.aha-comment__replies');
      const shareControls: HTMLElement | null = shadowRoot.querySelector(
        '.aha-comment__share-controls-hover',
      );
      if (shareControls && repliesElement) {
        const showControls = (shouldShow: boolean) => {
          shareControls.classList.add(shouldShow ? 'show' : 'hide');
          shareControls.classList.remove(shouldShow ? 'hide' : 'show');
        };
        this.host.addEventListener('mouseenter', () => showControls(true));
        this.host.addEventListener('mouseleave', () => showControls(false));
        repliesElement.addEventListener('mouseenter', () => showControls(false));
        repliesElement.addEventListener('mouseleave', () => showControls(true));
      }
    }
    // Listen for new reply events to close the reply editor if another one is opened
    document.addEventListener('aha-comment:toggle-reply-box', (event: Event) => {
      const customEvent = event as CustomEvent;
      const { target } = customEvent.detail;
      if (target !== this.host) {
        this.isReplying = false;
      }
    });
  }

  componentDidUpdate() {
    // In the case where a user is adding the first reply to a comment, we need to
    // remove the top and right borders of the trailing line to avoid a visual bug.
    if (this.isReplying && !this.hasReplies && !this.isChild) {
      const lineElement = this.host?.shadowRoot?.querySelector(
        '#avatar-trailing-line',
      ) as HTMLElement;
      if (lineElement) {
        lineElement.style.borderTop = 'none';
        lineElement.style.borderRight = 'none';
      }
    }
  }

  private handleOpenReplyBox(event: Event) {
    event.preventDefault();
    const newState = !this.isReplying;
    this.isReplying = newState;
    const newCommentEvent = new CustomEvent('aha-comment:toggle-reply-box', {
      bubbles: true,
      composed: true,
      detail: { target: this.host, isReplying: newState },
    });
    document.dispatchEvent(newCommentEvent);
  }

  get hasTrailingLine() {
    return this.hasReplies || this.isChild || this.isReplying;
  }

  get hasCurvedTrailingLine() {
    return this.hasReplies || this.isReplying;
  }

  render() {
    const shouldShowReplyButton = this.hasReplies && !this.isChild && !this.isReplying;
    const shouldShowNewReply = !this.isChild && this.isReplying;

    return (
      <Host>
        <div class="aha-comment">
          {/* AVATAR SIDE */}
          <div>
            <slot name="avatar" />
            <div
              id="avatar-trailing-line"
              class={classNames('aha-comment__avatar-trailing-line', {
                visible: this.hasTrailingLine,
                curved: this.hasCurvedTrailingLine,
              })}
            />
          </div>
          {/* BODY SIDE*/}
          <div class="aha-comment__body-side">
            {/* HEADER */}
            <div class="aha-comment__header">
              <span class="aha-comment__commenter-name">
                <slot name="commenter-name" />
              </span>
              <span class="aha-comment__header-controls">
                <span class={'aha-comment__share-controls-hover hide'}>
                  {!this.isReplying && this.repliesEnabled && !this.isChild && (
                    <aha-comment-button line>
                      <span aria-label="Reply" onClick={e => this.handleOpenReplyBox(e)}>
                        Reply
                      </span>
                    </aha-comment-button>
                  )}
                  <slot name="share-controls" />
                </span>
                <span class="aha-comment__share-controls-date">
                  {this.commentDate && <aha-date date={this.commentDate} format="medium" />}
                </span>
                <aha-tooltip placement="bottom">
                  {this.commentDate && (
                    <aha-date date={this.commentDate} format="medium" timeFormat="short" />
                  )}
                </aha-tooltip>
              </span>
            </div>
            {/* BODY */}
            <div class="aha-comment__body">
              <slot name="body" />
            </div>
          </div>
        </div>
        <div>
          <div class="aha-comment__replies">
            <slot name="replies" />
            {shouldShowReplyButton && (
              <div class="aha-comment__add-reply" onClick={e => this.handleOpenReplyBox(e)}>
                <aha-icon icon="fa-lg fa-regular fa-comment-plus"></aha-icon>
                Add reply
              </div>
            )}
            {shouldShowNewReply && <slot name="new-reply" />}
          </div>
        </div>
      </Host>
    );
  }
}
